import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useForm, Controller } from "react-hook-form";
import TechnologiesService from "../../api/technologiesService";
import techIcons, { getTechIconUrl } from "../../utils/techIcons";

// Convert the techIcons object to an array of option objects
const techIconOptions = Object.keys(techIcons)
  .map((key) => ({
    value: key,
    label: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize first letter
    iconUrl: techIcons[key],
  }))
  .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically

const EditTechnologyPage = () => {
  const { id } = useParams();
  const {
    control,
    handleSubmit,
    reset,
    setValue,
    formState: { errors },
    watch,
  } = useForm({
    defaultValues: {
      name: "",
      icon: "",
      selectedTech: "",
    },
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();

  const handleTechSelect = (event) => {
    const selectedValue = event.target.value;
    if (selectedValue) {
      const selected = techIconOptions.find(
        (option) => option.value === selectedValue
      );
      if (selected) {
        setValue("name", selected.label);
        setValue("icon", selected.iconUrl);
      }
    }
  };

  useEffect(() => {
    const fetchTechnology = async () => {
      try {
        setLoading(true);
        const technology = await TechnologiesService.getById(id);
        reset({
          name: technology.name,
          icon: technology.icon || "",
          selectedTech: "", // Default to empty since we're loading existing data
        });
      } catch (error) {
        console.error("Error fetching technology data:", error);
        alert("Could not load technology. Please try again.");
        navigate("/admin/technologies");
      } finally {
        setLoading(false);
      }
    };

    fetchTechnology();
  }, [id, navigate, reset]);

  const onSubmit = async (data) => {
    try {
      setSubmitting(true);
      // Remove the selectedTech field before sending to the API
      const { selectedTech, ...submissionData } = data;
      await TechnologiesService.update(id, submissionData);
      navigate("/admin/technologies");
    } catch (error) {
      console.error("Error updating technology:", error);
      alert("Failed to update technology. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", my: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Edit Technology
      </Typography>

      <Paper sx={{ p: 3, mt: 3 }}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <Grid container spacing={3}>
            <Grid item size={12}>
              <FormControl fullWidth>
                <InputLabel id="tech-select-label">
                  Quick Select Technology
                </InputLabel>
                <Controller
                  name="selectedTech"
                  control={control}
                  render={({ field }) => (
                    <Select
                      labelId="tech-select-label"
                      label="Quick Select Technology"
                      {...field}
                      onChange={(e) => {
                        field.onChange(e);
                        handleTechSelect(e);
                      }}
                    >
                      <MenuItem value="">
                        <em>Select a technology</em>
                      </MenuItem>
                      {techIconOptions.map((option) => (
                        <MenuItem key={option.value} value={option.value}>
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <img
                              src={option.iconUrl}
                              alt={option.label}
                              style={{ width: 24, height: 24, marginRight: 8 }}
                            />
                            {option.label}
                          </Box>
                        </MenuItem>
                      ))}
                    </Select>
                  )}
                />
                <FormHelperText>
                  Select from common technologies to auto-fill details
                </FormHelperText>
              </FormControl>
            </Grid>

            <Grid item size={12}>
              <Controller
                name="name"
                control={control}
                rules={{ required: "Technology name is required" }}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    required
                    label="Technology Name"
                    error={!!errors.name}
                    helperText={
                      errors.name?.message || "e.g., React, Node.js, TypeScript"
                    }
                  />
                )}
              />
            </Grid>

            <Grid item size={12}>
              <Controller
                name="icon"
                control={control}
                render={({ field }) => (
                  <TextField
                    {...field}
                    fullWidth
                    label="Icon URL"
                    error={!!errors.icon}
                    helperText={
                      errors.icon?.message || "URL to technology icon image"
                    }
                  />
                )}
              />
              {watch("icon") && (
                <Box sx={{ mt: 2, display: "flex", alignItems: "center" }}>
                  <Typography variant="body2" sx={{ mr: 2 }}>
                    Icon Preview:
                  </Typography>
                  <img
                    src={watch("icon")}
                    alt="Icon Preview"
                    style={{ width: 40, height: 40 }}
                    onError={(e) => {
                      e.target.style.display = "none";
                    }}
                  />
                </Box>
              )}
            </Grid>

            <Grid item size={12} sx={{ mt: 2, display: "flex", gap: 2 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={submitting}
              >
                {submitting ? (
                  <CircularProgress size={24} />
                ) : (
                  "Update Technology"
                )}
              </Button>
              <Button
                variant="outlined"
                onClick={() => navigate("/admin/technologies")}
              >
                Cancel
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Box>
  );
};

export default EditTechnologyPage;
